import { memo, useState, useRef, useEffect } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';

interface BaseNodeProps extends NodeProps {
  icon: string;
  label: string;
  color: string;
  description?: string;
  preview?: React.ReactNode;
  onClick?: () => void;
  onDelete?: () => void;
}

export const BaseNode = memo(({ id, data, icon, label, color, description, preview, onClick, onDelete }: BaseNodeProps & { id: string; data: any }) => {
  const isConfigured = data.config && Object.keys(data.config).length > 0;
  const [isEditing, setIsEditing] = useState(false);
  const [customLabel, setCustomLabel] = useState(data.customLabel || '');
  const inputRef = useRef<HTMLInputElement>(null);
  const { setNodes } = useReactFlow();

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleLabelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomLabel(e.target.value);
  };

  const handleLabelBlur = () => {
    setIsEditing(false);
    setNodes((nds) =>
      nds.map((node) =>
        node.id === id
          ? { ...node, data: { ...node.data, customLabel } }
          : node
      )
    );
  };

  const handleLabelKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleLabelBlur();
    } else if (e.key === 'Escape') {
      setCustomLabel(data.customLabel || '');
      setIsEditing(false);
    }
  };

  const displayLabel = customLabel || label;

  return (
    <div
      onClick={onClick}
      className="relative bg-white dark:bg-gray-800/90 border border-gray-200 dark:border-gray-600 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer min-w-[140px] group"
      style={{
        borderLeftWidth: 4,
        borderLeftColor: color,
      }}
    >
      {/* Botão de deletar */}
      {onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center hover:bg-red-600 z-10 shadow-md"
          title="Deletar node"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}

      {/* Handle de entrada */}
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 !bg-gray-300 dark:!bg-gray-500 !border-2 !border-white dark:!border-gray-700 !shadow-sm"
      />

      {/* Conteúdo do node */}
      <div className="px-3 py-2.5">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm">{icon}</span>
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <input
                ref={inputRef}
                type="text"
                value={customLabel}
                onChange={handleLabelChange}
                onBlur={handleLabelBlur}
                onKeyDown={handleLabelKeyDown}
                className="w-full px-1.5 py-0.5 text-sm font-semibold text-gray-900 dark:text-white border border-emerald-400 dark:border-emerald-500 rounded-lg bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div onClick={handleLabelClick} className="cursor-text min-w-0">
                <div className="font-semibold text-sm text-gray-900 dark:text-white hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors truncate">
                  {displayLabel}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{label}</div>
              </div>
            )}
          </div>
        </div>

        {description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-snug whitespace-pre-line max-w-[240px]">{description}</p>
        )}

        {/* Preview de mídia */}
        {preview && (
          <div className="mt-1.5">
            {preview}
          </div>
        )}

        {isConfigured && (
          <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-xs font-medium text-emerald-700 dark:text-emerald-300">
            <i className="fas fa-check text-emerald-600 dark:text-emerald-400" aria-hidden />
            Configurado
          </div>
        )}

        {!isConfigured && (
          <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs font-medium text-amber-700 dark:text-amber-300">
            <i className="fas fa-info-circle text-amber-600 dark:text-amber-400" aria-hidden />
            Clique para configurar
          </div>
        )}
      </div>

      {/* Handle de saída */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-3 h-3 !bg-gray-300 dark:!bg-gray-500 !border-2 !border-white dark:!border-gray-700 !shadow-sm"
      />
    </div>
  );
});

BaseNode.displayName = 'BaseNode';
